import React, {useEffect, useState} from 'react';
import {Link, useNavigate} from 'react-router-dom';
import '../assets/css/CheckoutForm.css';
import {
    createSelectedPricingPackage,
    createUser,
    deleteSelectedPricingPackage,
    getNewUser,
    getSelectedPricingPackage,
    getUser,
    updateUser
} from "../auth/Auth";
import Loading from "../components/Loading";
import {postRequest} from "../routes/Routes";
import {loadStripe} from '@stripe/stripe-js';
import {Elements, PaymentElement, useElements, useStripe} from '@stripe/react-stripe-js';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const CheckoutForm = ({updatePaymentOption, isTransaction}) => {
    const [message, setMessage] = useState(''),
        [showPopup, setShowPopup] = useState(false),
        [user, setUser] = useState(null),
        [loading, setLoading] = useState(false),
        [selectedPricingPackage, setSelectedPricingPackage] = useState(null),
        [transaction, setTransaction] = useState(null);

    const navigate = useNavigate(),
        stripe = useStripe(),
        elements = useElements();

    useEffect(() => {
        getNewUser().then((data) => {
            if (data) {
                setUser(data);
            } else {
                getUser().then((data) => {
                    setUser(data);
                }).catch(() => {
                    setUser(null);
                });
            }
        }).catch(() => {
            setUser(null);
        });
        getSelectedPricingPackage(!isTransaction).then((data) => {
            setSelectedPricingPackage(data);
        }).catch(() => {
            setSelectedPricingPackage(null);
        });
    }, []);

    useEffect(() => {
        if (user && selectedPricingPackage) {
            updatePaymentOption(selectedPricingPackage, user.customer_session_client_secret);
        }
    }, [selectedPricingPackage, user]);

    useEffect(() => {
        if (isTransaction && stripe) {
            const clientSecret = new URLSearchParams(window.location.search).get('payment_intent_client_secret');
            stripe.retrievePaymentIntent(clientSecret).then(({paymentIntent}) => {
                let tempTransaction;
                switch (paymentIntent.status) {
                    case 'succeeded':
                        tempTransaction = {
                            success: true,
                            message: 'Your payment was successful.',
                        }
                        break;

                    case 'processing':
                        tempTransaction = {
                            success: true,
                            message: 'Payment processing.',
                        }
                        break;

                    case 'requires_payment_method':
                        tempTransaction = {
                            success: false,
                            message: 'Payment failed.',
                        }
                        break;

                    default:
                        tempTransaction = {
                            success: false,
                            message: 'An error occurred!',
                        }
                        break;
                }
                setTransaction(tempTransaction);
            });
        }
    }, [stripe]);

    const popupStyle = {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        zIndex: 99,
    };

    const popupContentStyle = {
        backgroundColor: 'white',
        padding: '20px',
        borderRadius: '8px',
        textAlign: 'center',
    };

    const buttonStyle = {
        display: 'flex',
        width: '327px',
        padding: '9px 24px',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '10px',
        cursor: 'pointer',
        marginTop: '20px',
        color: '#FFF',
        fontFamily: "Orelega One",
        fontSize: '16px',
        fontWeight: '400',
        lineHeight: '22px',
        textTransform: 'capitalize',
        borderRadius: '50px',
        background: '#28A56E',
        border: 'none',
    };

    const buttonStyleFail = {
        display: 'flex',
        width: '327px',
        padding: '9px 24px',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '10px',
        cursor: 'pointer',
        marginTop: '20px',
        color: '#FFF',
        fontFamily: "Orelega One",
        fontSize: '16px',
        fontWeight: '400',
        lineHeight: '22px',
        textTransform: 'capitalize',
        borderRadius: '50px',
        background: '#F65C5C',
        border: 'none',
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setMessage(`Success`);
        setShowPopup(true);
    };

    const handlePlanChange = (e) => {
        createSelectedPricingPackage({
            ...selectedPricingPackage,
            annualSelected: e.target.value === "annual"
        }).then(() => {
            setSelectedPricingPackage({...selectedPricingPackage, annualSelected: e.target.value === "annual"});
        });
    };

    const handleClosePopup = () => {
        setShowPopup(false);
    };

    const handleConfirm = async (e) => {
        e.preventDefault();
        setLoading(true);
        let tempUser = user;
        if (!user.hasOwnProperty("id")) {
            let values = {
                ...user,
                endpoint: '/api/user'
            };
            await postRequest(values).then(async response => {
                if (response.status === 200) {
                    if (response.data.error) {
                        setMessage(`Error: ${response.data.message}`);
                        setShowPopup(true);
                    } else {
                        await createUser(response.data.user).then(async () => {
                            await getUser().then((data) => {
                                tempUser = data;
                            }).catch(null);
                        });
                    }
                } else {
                    setMessage('Error: An error occurred!');
                    setShowPopup(true);
                }
            });
        }
        let values = {
            pricing_package: selectedPricingPackage,
            token: tempUser.token,
            endpoint: '/api/user-pricing-package/subscribe'
        };
        postRequest(values).then(async response => {
            if (response.status === 200) {
                if (response.data.error) {
                    setMessage(`Error: ${response.data.message}`);
                    setShowPopup(true);
                } else {
                    let tempUserCookie = response.data.user;
                    tempUserCookie.time = tempUser.time;
                    tempUserCookie.token = tempUser.token;
                    tempUserCookie.customer_session_client_secret = tempUser.customer_session_client_secret;
                    updateUser(tempUserCookie).then(() => {
                        deleteSelectedPricingPackage().then(null);
                    });

                    // Trigger form validation and wallet collection
                    const {error: submitError} = await elements.submit();
                    if (submitError) {
                        setMessage(`Error: ${submitError.message}`);
                        setShowPopup(true);
                        return;
                    }

                    const confirmIntent = response.data.type === "setup" ? stripe.confirmSetup : stripe.confirmPayment,
                        clientSecret = response.data.clientSecret;

                    if (clientSecret) {
                        // Confirm the Intent using the details collected by the Payment Element
                        const {error} = await confirmIntent({
                            elements,
                            clientSecret,
                            confirmParams: {
                                return_url: process.env.REACT_APP_STRIPE_RETURN_URL,
                            },
                        });

                        if (error) {
                            setMessage(`Error: ${error.message}`);
                            setShowPopup(true);
                        }
                    } else {
                        navigate('/profile');
                    }
                }
            } else {
                setMessage('Error: An error occurred!');
                setShowPopup(true);
            }
            setLoading(false);
        });
    };

    if (user && (selectedPricingPackage || (isTransaction && transaction))) {
        return (
            <div className='body-main'>
                <div className="profile-checkout-container series-container">
                    <h2 className="profile-checkout-heading">Checkout</h2>
                    <p className="profile-checkout-subheading">
                        Update your pricing plan and enjoy the various benefits it offers.
                    </p>
                    {selectedPricingPackage &&
                    <div className='flex-checkout'>
                        <form className="profile-checkout-form" onSubmit={handleConfirm}>
                            <div className="profile-checkout-billing-info">
                                <div className='checkout-txt-box'>
                                    <span className='main-box-checkout'>Billing Information</span>
                                    <span
                                        className='sub-box-checkout'>Add your billing details and contact details</span>
                                </div>
                                <label>Contact Email</label>
                                <input
                                    type="email"
                                    name="email"
                                    value={user.email}
                                    className="profile-checkout-input"
                                    placeholder='janet123@gmail.com'
                                    disabled
                                />
                            </div>
                            <div className="profile-checkout-card-details">
                                <div className='checkout-txt-box'>
                                    <span className='main-box-checkout'>Card Details</span>
                                    <span className='sub-box-checkout'>Add your card details</span>
                                </div>
                                <PaymentElement/>
                            </div>
                            <button type="submit" className="profile-checkout-submit-btn">
                                Confirm & Pay
                            </button>
                            <span className='specil-checkout-note'>
                            You can cancel your plan at any time. Purchases made by credit card can’t be refunded. <br/><br/>By submitting this
                            form, you confirm that you agree to our  <Link
                                to="/legal">Terms of Service</Link> and  <Link to="/legal">Privacy Policy</Link>.
                        </span>
                        </form>
                        <section className="profile-checkout-pricing">
                            <div className="profile-checkout-plan-header">
                                <h2 className="profile-checkout-plan-title">{selectedPricingPackage.name}</h2>
                                <button className="profile-checkout-plan-change" onClick={() => navigate("/price")}>
                                    Change
                                </button>
                            </div>

                            <div className="profile-checkout-plan-options">
                                <label
                                    className={`profile-checkout-plan-option ${selectedPricingPackage.annualSelected ? 'active' : ''}`}>
                                    <div>
                                        <input
                                            type="radio"
                                            name="plan"
                                            value="annual"
                                            checked={selectedPricingPackage.annualSelected}
                                            onChange={handlePlanChange}
                                        />
                                        <span className="profile-checkout-plan-label">Annual <span
                                            className="profile-checkout-save-badge">Save {Math.round(((((selectedPricingPackage.monthly_price - selectedPricingPackage.annual_price) / selectedPricingPackage.monthly_price) * 100) + Number.EPSILON) * 100) / 100}%</span></span>
                                    </div>
                                    <span
                                        className="profile-checkout-plan-price"><span>€{(selectedPricingPackage.annual_price * 12).toFixed(2)} </span>/ per year</span>
                                </label>

                                <label
                                    className={`profile-checkout-plan-option ${!selectedPricingPackage.annualSelected ? 'active' : ''}`}>
                                    <div>
                                        <input
                                            type="radio"
                                            name="plan"
                                            value="monthly"
                                            checked={!selectedPricingPackage.annualSelected}
                                            onChange={handlePlanChange}
                                        />
                                        <span className="profile-checkout-plan-label">Monthly</span>
                                    </div>
                                    <span
                                        className="profile-checkout-plan-price"><span>€{selectedPricingPackage.monthly_price} </span>/ per month</span>
                                </label>
                            </div>

                            <div className="profile-checkout-plan-description">
                                Your subscription will renew automatically
                                every {selectedPricingPackage.annualSelected ? "year" : "month"} as one payment of
                                €{selectedPricingPackage.annualSelected ? (selectedPricingPackage.annual_price * 12).toFixed(2) : selectedPricingPackage.monthly_price}.
                                Cancel it anytime from your subscription settings. By clicking "Confirm and pay" you
                                agree to the <Link to="/legal" className="profile-checkout-terms-link">Terms and
                                Conditions.</Link>
                            </div>

                            <div className="profile-checkout-plan-summary">
                                <div className="profile-checkout-summary-item">
                                    <span>Net Price</span>
                                    <span>€{selectedPricingPackage.annualSelected ? (selectedPricingPackage.annual_price * 12).toFixed(2) : selectedPricingPackage.monthly_price}</span>
                                </div>
                                <div className="profile-checkout-summary-item">
                                    <span>Sales Tax</span>
                                    <span>€0</span>
                                </div>
                                <div className="profile-checkout-summary-total">
                                    <span>Due Today</span>
                                    <span>€{selectedPricingPackage.annualSelected ? (selectedPricingPackage.annual_price * 12).toFixed(2) : selectedPricingPackage.monthly_price}</span>
                                </div>
                            </div>
                        </section>
                    </div>
                    }

                    {showPopup && (
                        message.includes('Success') ? (
                            <>
                                <div style={popupStyle}>
                                    <div style={popupContentStyle}>
                                        <svg xmlns="http://www.w3.org/2000/svg" width="73" height="72"
                                             viewBox="0 0 73 72" fill="none">
                                            <path
                                                d="M34.3184 2.39065C33.0528 2.58753 31.2247 3.19221 29.9591 3.82502C25.6278 5.99065 22.6466 10.1813 21.9997 14.9766C21.7184 17.1563 21.9153 18.0281 22.9138 18.7594C23.4622 19.1672 24.7841 19.1672 25.3325 18.7594C26.1059 18.1828 26.2606 17.8313 26.4013 16.2985C26.6544 13.5563 27.5403 11.6156 29.3825 9.75939C30.6481 8.49377 31.8434 7.74846 33.5169 7.20002C34.4591 6.89065 34.8387 6.8344 36.4981 6.8344C38.1575 6.8344 38.5372 6.89065 39.4794 7.20002C41.1528 7.74846 42.3481 8.49377 43.6137 9.75939C45.4559 11.6156 46.3419 13.5563 46.595 16.2985C46.7356 17.8313 46.8903 18.1828 47.6637 18.7594C48.2122 19.1672 49.5341 19.1672 50.0825 18.7594C51.0809 18.0281 51.2778 17.1703 50.9825 15.0047C50.1669 8.94377 45.92 4.21877 39.9434 2.65784C38.6497 2.3344 35.6684 2.17971 34.3184 2.39065Z"
                                                fill="#28A56E"/>
                                            <path
                                                d="M16.6719 22.725C13.6203 23.3718 10.8641 25.9593 10.0062 28.9687C9.68281 30.1218 5 59.9343 5 60.8625C5.01406 64.0968 7.03906 67.3172 9.95 68.7515C12.0734 69.8062 10.4141 69.75 36.5 69.75C62.6141 69.75 60.9125 69.8062 63.0781 68.7375C65.9469 67.3312 67.9859 64.0687 68 60.8625C68 59.9343 63.3172 30.1218 62.9937 28.9687C62.3469 26.6906 60.5469 24.5531 58.4375 23.4984C56.3 22.4578 57.4672 22.5 36.4297 22.514C20.8625 22.514 17.4312 22.5562 16.6719 22.725ZM47.8625 36.3797C48.7344 37.0406 49.1 38.1515 48.7203 39.0515C48.6078 39.3047 45.2328 43.1859 41.2109 47.6859C35.8531 53.7047 33.7859 55.9265 33.4203 56.0672C32.8297 56.3203 31.8734 56.25 31.3812 55.9125C30.9031 55.6031 24.5187 47.7703 24.2797 47.2078C23.8578 46.1953 24.3922 44.9015 25.4469 44.4234C26.1641 44.1 26.4875 44.1 27.2188 44.3812C27.6969 44.5781 28.2172 45.1265 30.1859 47.5453C31.5078 49.1625 32.6187 50.4843 32.6328 50.4843C32.6609 50.4843 35.5156 47.3062 38.975 43.439C42.8 39.164 45.4437 36.3093 45.6969 36.1968C46.2734 35.9296 47.3984 36.0281 47.8625 36.3797Z"
                                                fill="#28A56E"/>
                                        </svg>
                                        <h2>Success!</h2>
                                        <p>Please continue to payment.</p>
                                        <button style={buttonStyle} onClick={handleConfirm}>
                                            {loading ?
                                                <span className="spinner-grow spinner-grow-sm" role="status"
                                                      aria-hidden="true"/>
                                                : null
                                            }
                                            Confirm & Pay
                                        </button>
                                    </div>
                                    <button className="close-btn-com" onClick={handleClosePopup}>X</button>
                                </div>
                            </>
                        ) : (
                            <>
                                <div style={popupStyle}>
                                    <div style={popupContentStyle}>
                                        <svg xmlns="http://www.w3.org/2000/svg" width="73" height="72"
                                             viewBox="0 0 73 72" fill="none">
                                            <path
                                                d="M17.6266 22.4807C17.7625 22.48 17.8984 22.4793 18.0385 22.4786C18.2619 22.4785 18.2619 22.4785 18.4898 22.4784C18.6483 22.4778 18.8068 22.4771 18.9702 22.4765C19.5034 22.4746 20.0367 22.4738 20.57 22.4729C20.9515 22.4719 21.3331 22.4706 21.7147 22.4694C22.8616 22.466 24.0085 22.4639 25.1554 22.4621C25.4796 22.4616 25.8039 22.461 26.1281 22.4604C28.1435 22.4569 30.1589 22.4538 32.1743 22.452C32.6411 22.4516 33.1079 22.4512 33.5746 22.4508C33.7486 22.4506 33.7486 22.4506 33.9261 22.4505C35.8024 22.4487 37.6787 22.444 39.5549 22.4382C41.4835 22.4322 43.412 22.4289 45.3405 22.4281C46.4224 22.4277 47.5043 22.426 48.5862 22.4214C49.5083 22.4174 50.4303 22.4159 51.3524 22.4175C51.8221 22.4182 52.2917 22.4174 52.7613 22.4143C56.0059 22.3939 58.4555 22.7894 60.91 25.0917C62.8985 27.2055 63.3562 29.6038 63.7807 32.3795C63.8338 32.7164 63.8871 33.0533 63.9406 33.3902C64.0277 33.9397 64.1146 34.4892 64.2005 35.0389C64.4334 36.528 64.6733 38.0159 64.9157 39.5035C65.268 41.6666 65.617 43.8302 65.9633 45.9942C66.1632 47.243 66.3642 48.4916 66.5675 49.7399C66.7489 50.8536 66.9284 51.9676 67.1057 53.0819C67.1983 53.6639 67.2918 54.2458 67.3876 54.8273C68.543 61.8507 68.543 61.8507 67.4375 64.2656C67.3708 64.4161 67.3041 64.5666 67.2354 64.7216C66.3619 66.5062 65.1056 67.8136 63.3594 68.7656C63.2144 68.8494 63.2144 68.8494 63.0666 68.9348C61.6697 69.6973 60.2429 69.8118 58.6773 69.8045C58.4777 69.8051 58.2781 69.8059 58.0786 69.8068C57.534 69.8088 56.9895 69.8083 56.445 69.8072C55.8562 69.8066 55.2674 69.8083 54.6787 69.8098C53.5267 69.8122 52.3747 69.8123 51.2227 69.8116C50.286 69.811 49.3493 69.8112 48.4126 69.8119C48.2791 69.812 48.1456 69.8122 48.008 69.8123C47.7367 69.8125 47.4654 69.8127 47.1942 69.8129C44.6524 69.8148 42.1107 69.8141 39.5689 69.8125C37.2459 69.8111 34.923 69.8129 32.6 69.8162C30.212 69.8197 27.824 69.821 25.4361 69.8201C24.0965 69.8196 22.7569 69.8199 21.4174 69.8224C20.277 69.8245 19.1367 69.8245 17.9963 69.8221C17.4151 69.8209 16.8339 69.8206 16.2526 69.8227C15.7195 69.8247 15.1864 69.824 14.6533 69.8213C14.3713 69.8205 14.0893 69.8225 13.8074 69.8246C11.3146 69.8045 9.10586 68.8095 7.34283 67.0628C5.74061 65.3217 4.89424 63.0979 4.85938 60.75C4.88955 60.3661 4.9254 59.9873 4.97055 59.6053C4.9832 59.4954 4.99584 59.3856 5.00887 59.2724C5.16459 57.9608 5.37001 56.6571 5.57671 55.3528C5.62022 55.0755 5.66368 54.7983 5.7071 54.521C5.82394 53.776 5.94151 53.0311 6.0593 52.2863C6.13306 51.8196 6.20667 51.3529 6.28023 50.8862C6.53739 49.2547 6.79519 47.6232 7.05355 45.9918C7.29356 44.4761 7.53214 42.9603 7.77006 41.4443C7.97525 40.1373 8.18148 38.8305 8.38849 37.5238C8.51174 36.7457 8.63458 35.9675 8.75637 35.1892C8.87084 34.4577 8.98661 33.7265 9.10334 32.9954C9.14575 32.7285 9.18771 32.4614 9.22917 32.1943C9.73174 28.9605 10.5541 26.1095 13.2969 24.0469C14.5616 23.2351 16.0964 22.485 17.6266 22.4807ZM26.7441 37.0723C26.4419 37.7 26.3737 38.2683 26.5156 38.9531C27.014 39.8464 27.765 40.5193 28.4866 41.2328C28.6246 41.3703 28.7625 41.5078 28.9003 41.6454C29.2613 42.0052 29.6231 42.3643 29.985 42.7231C30.3551 43.0903 30.7243 43.4582 31.0936 43.8261C31.8168 44.5463 32.5409 45.2656 33.2656 45.9844C33.0546 46.4789 32.7702 46.7937 32.3889 47.1698C32.266 47.2918 32.143 47.4139 32.0163 47.5396C31.9507 47.6041 31.885 47.6686 31.8173 47.7351C31.611 47.9378 31.4056 48.1414 31.2003 48.3453C30.6906 48.8513 30.1793 49.3557 29.6681 49.8603C29.2357 50.287 28.804 50.7144 28.373 51.1425C28.1709 51.3428 27.9679 51.5423 27.7649 51.7417C27.642 51.8637 27.519 51.9857 27.3923 52.1115C27.2297 52.2719 27.2297 52.2719 27.0637 52.4356C26.5157 53.0492 26.4528 53.5536 26.4717 54.356C26.5606 55.0583 26.9415 55.4233 27.4473 55.8809C28.0749 56.1831 28.6433 56.2514 29.3281 56.1094C30.2214 55.611 30.8943 54.86 31.6078 54.1384C31.7452 54.0005 31.8828 53.8625 32.0203 53.7247C32.3802 53.3637 32.7393 53.0019 33.0981 52.64C33.4653 52.2699 33.8332 51.9007 34.2011 51.5314C34.9213 50.8082 35.6406 50.0841 36.3594 49.3594C36.8539 49.5704 37.1687 49.8548 37.5448 50.2361C37.6668 50.359 37.7888 50.482 37.9146 50.6087C37.9791 50.6743 38.0436 50.74 38.1101 50.8077C38.3128 51.014 38.5164 51.2195 38.7203 51.4247C39.2263 51.9344 39.7307 52.4457 40.2352 52.9569C40.662 53.3893 41.0894 53.821 41.5175 54.252C41.7178 54.4542 41.9173 54.6571 42.1167 54.8601C42.2387 54.9831 42.3607 55.106 42.4865 55.2327C42.6469 55.3953 42.6469 55.3953 42.8106 55.5613C43.4242 56.1093 43.9286 56.1722 44.731 56.1533C45.4333 56.0644 45.7983 55.6835 46.2559 55.1777C46.5581 54.5501 46.6264 53.9817 46.4844 53.2969C45.986 52.4036 45.235 51.7307 44.5134 51.0172C44.3754 50.8798 44.2375 50.7422 44.0997 50.6047C43.7387 50.2448 43.3769 49.8858 43.015 49.5269C42.6449 49.1598 42.2757 48.7918 41.9064 48.4239C41.1832 47.7037 40.4591 46.9844 39.7344 46.2656C39.9454 45.7711 40.2298 45.4563 40.6111 45.0802C40.734 44.9582 40.857 44.8362 40.9837 44.7105C41.0493 44.6459 41.115 44.5814 41.1827 44.515C41.389 44.3122 41.5944 44.1086 41.7997 43.9047C42.3094 43.3987 42.8207 42.8943 43.3319 42.3898C43.7643 41.963 44.196 41.5356 44.627 41.1075C44.8291 40.9072 45.0321 40.7077 45.2351 40.5083C45.358 40.3863 45.481 40.2643 45.6077 40.1386C45.7703 39.9781 45.7703 39.9781 45.9363 39.8144C46.4843 39.2008 46.5472 38.6964 46.5283 37.8941C46.4394 37.1917 46.0585 36.8268 45.5527 36.3692C44.9251 36.0669 44.3567 35.9987 43.6719 36.1406C42.7786 36.6391 42.1057 37.39 41.3922 38.1116C41.2548 38.2496 41.1172 38.3875 40.9797 38.5253C40.6198 38.8863 40.2607 39.2481 39.9019 39.6101C39.5347 39.9801 39.1668 40.3493 38.7989 40.7186C38.0787 41.4419 37.3594 42.166 36.6406 42.8906C36.1461 42.6796 35.8313 42.3952 35.4552 42.0139C35.3332 41.891 35.2112 41.768 35.0854 41.6414C35.0209 41.5757 34.9564 41.51 34.8899 41.4423C34.6872 41.236 34.4836 41.0306 34.2797 40.8253C33.7737 40.3156 33.2693 39.8043 32.7648 39.2931C32.338 38.8608 31.9106 38.429 31.4825 37.998C31.2822 37.7959 31.0827 37.5929 30.8833 37.3899C30.7613 37.267 30.6393 37.144 30.5135 37.0173C30.4066 36.9089 30.2996 36.8005 30.1894 36.6887C29.5758 36.1407 29.0714 36.0778 28.269 36.0967C27.5667 36.1856 27.2017 36.5665 26.7441 37.0723Z"
                                                fill="#F65C5C"/>
                                            <path
                                                d="M41.0007 2.81252C41.0913 2.84341 41.1819 2.87429 41.2752 2.90611C44.9075 4.17335 48.0192 6.80154 49.7239 10.2678C50.6178 12.1825 51.1475 14.1237 51.1697 16.2422C51.1737 16.3722 51.1778 16.5022 51.182 16.6361C51.1896 17.4487 51.113 17.9213 50.5632 18.5625C50.0261 19.0728 49.5953 19.1686 48.8757 19.1953C48.1561 19.1686 47.7253 19.0728 47.1882 18.5625C46.6421 17.8791 46.542 17.3805 46.4939 16.541C46.2404 13.557 44.9776 10.9619 42.6882 9.00002C40.4976 7.40238 37.917 6.66023 35.2175 6.96096C32.4101 7.40189 30.0048 8.81891 28.3038 11.1237C27.167 12.8737 26.7266 14.5448 26.5392 16.5945C26.4339 17.631 26.2284 18.1681 25.4617 18.8965C24.7353 19.2337 24.0694 19.231 23.282 19.125C22.5837 18.8008 22.2392 18.3904 21.8757 17.7188C21.7942 17.2569 21.7956 16.8069 21.8054 16.3389C21.8071 16.2086 21.8088 16.0784 21.8106 15.9441C21.9228 11.9793 23.9072 8.49408 26.7207 5.79092C30.4016 2.398 36.2746 1.192 41.0007 2.81252Z"
                                                fill="#F65C5C"/>
                                        </svg>
                                        <h2>Failed!</h2>
                                        <p>{message.split("Error: ")[1]}<br/>Try again.</p>
                                        <button style={buttonStyleFail} onClick={handleClosePopup}>
                                            Back to Checkout
                                        </button>
                                    </div>
                                    <button className="close-btn-com" onClick={handleClosePopup}>X</button>
                                </div>
                            </>
                        )
                    )}
                    {transaction && (
                        transaction.success ? (
                            <>
                                <div style={popupStyle}>
                                    <div style={popupContentStyle}>
                                        <svg xmlns="http://www.w3.org/2000/svg" width="73" height="72"
                                             viewBox="0 0 73 72" fill="none">
                                            <path
                                                d="M34.3184 2.39065C33.0528 2.58753 31.2247 3.19221 29.9591 3.82502C25.6278 5.99065 22.6466 10.1813 21.9997 14.9766C21.7184 17.1563 21.9153 18.0281 22.9138 18.7594C23.4622 19.1672 24.7841 19.1672 25.3325 18.7594C26.1059 18.1828 26.2606 17.8313 26.4013 16.2985C26.6544 13.5563 27.5403 11.6156 29.3825 9.75939C30.6481 8.49377 31.8434 7.74846 33.5169 7.20002C34.4591 6.89065 34.8387 6.8344 36.4981 6.8344C38.1575 6.8344 38.5372 6.89065 39.4794 7.20002C41.1528 7.74846 42.3481 8.49377 43.6137 9.75939C45.4559 11.6156 46.3419 13.5563 46.595 16.2985C46.7356 17.8313 46.8903 18.1828 47.6637 18.7594C48.2122 19.1672 49.5341 19.1672 50.0825 18.7594C51.0809 18.0281 51.2778 17.1703 50.9825 15.0047C50.1669 8.94377 45.92 4.21877 39.9434 2.65784C38.6497 2.3344 35.6684 2.17971 34.3184 2.39065Z"
                                                fill="#28A56E"/>
                                            <path
                                                d="M16.6719 22.725C13.6203 23.3718 10.8641 25.9593 10.0062 28.9687C9.68281 30.1218 5 59.9343 5 60.8625C5.01406 64.0968 7.03906 67.3172 9.95 68.7515C12.0734 69.8062 10.4141 69.75 36.5 69.75C62.6141 69.75 60.9125 69.8062 63.0781 68.7375C65.9469 67.3312 67.9859 64.0687 68 60.8625C68 59.9343 63.3172 30.1218 62.9937 28.9687C62.3469 26.6906 60.5469 24.5531 58.4375 23.4984C56.3 22.4578 57.4672 22.5 36.4297 22.514C20.8625 22.514 17.4312 22.5562 16.6719 22.725ZM47.8625 36.3797C48.7344 37.0406 49.1 38.1515 48.7203 39.0515C48.6078 39.3047 45.2328 43.1859 41.2109 47.6859C35.8531 53.7047 33.7859 55.9265 33.4203 56.0672C32.8297 56.3203 31.8734 56.25 31.3812 55.9125C30.9031 55.6031 24.5187 47.7703 24.2797 47.2078C23.8578 46.1953 24.3922 44.9015 25.4469 44.4234C26.1641 44.1 26.4875 44.1 27.2188 44.3812C27.6969 44.5781 28.2172 45.1265 30.1859 47.5453C31.5078 49.1625 32.6187 50.4843 32.6328 50.4843C32.6609 50.4843 35.5156 47.3062 38.975 43.439C42.8 39.164 45.4437 36.3093 45.6969 36.1968C46.2734 35.9296 47.3984 36.0281 47.8625 36.3797Z"
                                                fill="#28A56E"/>
                                        </svg>
                                        <h2>Success!</h2>
                                        <p>{transaction.message}</p>
                                        <button style={buttonStyle} onClick={() => navigate('/profile')}>
                                            {loading ?
                                                <span className="spinner-grow spinner-grow-sm" role="status"
                                                      aria-hidden="true"/>
                                                : null
                                            }
                                            Continue
                                        </button>
                                    </div>
                                    <button className="close-btn-com" onClick={() => navigate('/profile')}>X</button>
                                </div>
                            </>
                        ) : (
                            <>
                                <div style={popupStyle}>
                                    <div style={popupContentStyle}>
                                        <svg xmlns="http://www.w3.org/2000/svg" width="73" height="72"
                                             viewBox="0 0 73 72" fill="none">
                                            <path
                                                d="M17.6266 22.4807C17.7625 22.48 17.8984 22.4793 18.0385 22.4786C18.2619 22.4785 18.2619 22.4785 18.4898 22.4784C18.6483 22.4778 18.8068 22.4771 18.9702 22.4765C19.5034 22.4746 20.0367 22.4738 20.57 22.4729C20.9515 22.4719 21.3331 22.4706 21.7147 22.4694C22.8616 22.466 24.0085 22.4639 25.1554 22.4621C25.4796 22.4616 25.8039 22.461 26.1281 22.4604C28.1435 22.4569 30.1589 22.4538 32.1743 22.452C32.6411 22.4516 33.1079 22.4512 33.5746 22.4508C33.7486 22.4506 33.7486 22.4506 33.9261 22.4505C35.8024 22.4487 37.6787 22.444 39.5549 22.4382C41.4835 22.4322 43.412 22.4289 45.3405 22.4281C46.4224 22.4277 47.5043 22.426 48.5862 22.4214C49.5083 22.4174 50.4303 22.4159 51.3524 22.4175C51.8221 22.4182 52.2917 22.4174 52.7613 22.4143C56.0059 22.3939 58.4555 22.7894 60.91 25.0917C62.8985 27.2055 63.3562 29.6038 63.7807 32.3795C63.8338 32.7164 63.8871 33.0533 63.9406 33.3902C64.0277 33.9397 64.1146 34.4892 64.2005 35.0389C64.4334 36.528 64.6733 38.0159 64.9157 39.5035C65.268 41.6666 65.617 43.8302 65.9633 45.9942C66.1632 47.243 66.3642 48.4916 66.5675 49.7399C66.7489 50.8536 66.9284 51.9676 67.1057 53.0819C67.1983 53.6639 67.2918 54.2458 67.3876 54.8273C68.543 61.8507 68.543 61.8507 67.4375 64.2656C67.3708 64.4161 67.3041 64.5666 67.2354 64.7216C66.3619 66.5062 65.1056 67.8136 63.3594 68.7656C63.2144 68.8494 63.2144 68.8494 63.0666 68.9348C61.6697 69.6973 60.2429 69.8118 58.6773 69.8045C58.4777 69.8051 58.2781 69.8059 58.0786 69.8068C57.534 69.8088 56.9895 69.8083 56.445 69.8072C55.8562 69.8066 55.2674 69.8083 54.6787 69.8098C53.5267 69.8122 52.3747 69.8123 51.2227 69.8116C50.286 69.811 49.3493 69.8112 48.4126 69.8119C48.2791 69.812 48.1456 69.8122 48.008 69.8123C47.7367 69.8125 47.4654 69.8127 47.1942 69.8129C44.6524 69.8148 42.1107 69.8141 39.5689 69.8125C37.2459 69.8111 34.923 69.8129 32.6 69.8162C30.212 69.8197 27.824 69.821 25.4361 69.8201C24.0965 69.8196 22.7569 69.8199 21.4174 69.8224C20.277 69.8245 19.1367 69.8245 17.9963 69.8221C17.4151 69.8209 16.8339 69.8206 16.2526 69.8227C15.7195 69.8247 15.1864 69.824 14.6533 69.8213C14.3713 69.8205 14.0893 69.8225 13.8074 69.8246C11.3146 69.8045 9.10586 68.8095 7.34283 67.0628C5.74061 65.3217 4.89424 63.0979 4.85938 60.75C4.88955 60.3661 4.9254 59.9873 4.97055 59.6053C4.9832 59.4954 4.99584 59.3856 5.00887 59.2724C5.16459 57.9608 5.37001 56.6571 5.57671 55.3528C5.62022 55.0755 5.66368 54.7983 5.7071 54.521C5.82394 53.776 5.94151 53.0311 6.0593 52.2863C6.13306 51.8196 6.20667 51.3529 6.28023 50.8862C6.53739 49.2547 6.79519 47.6232 7.05355 45.9918C7.29356 44.4761 7.53214 42.9603 7.77006 41.4443C7.97525 40.1373 8.18148 38.8305 8.38849 37.5238C8.51174 36.7457 8.63458 35.9675 8.75637 35.1892C8.87084 34.4577 8.98661 33.7265 9.10334 32.9954C9.14575 32.7285 9.18771 32.4614 9.22917 32.1943C9.73174 28.9605 10.5541 26.1095 13.2969 24.0469C14.5616 23.2351 16.0964 22.485 17.6266 22.4807ZM26.7441 37.0723C26.4419 37.7 26.3737 38.2683 26.5156 38.9531C27.014 39.8464 27.765 40.5193 28.4866 41.2328C28.6246 41.3703 28.7625 41.5078 28.9003 41.6454C29.2613 42.0052 29.6231 42.3643 29.985 42.7231C30.3551 43.0903 30.7243 43.4582 31.0936 43.8261C31.8168 44.5463 32.5409 45.2656 33.2656 45.9844C33.0546 46.4789 32.7702 46.7937 32.3889 47.1698C32.266 47.2918 32.143 47.4139 32.0163 47.5396C31.9507 47.6041 31.885 47.6686 31.8173 47.7351C31.611 47.9378 31.4056 48.1414 31.2003 48.3453C30.6906 48.8513 30.1793 49.3557 29.6681 49.8603C29.2357 50.287 28.804 50.7144 28.373 51.1425C28.1709 51.3428 27.9679 51.5423 27.7649 51.7417C27.642 51.8637 27.519 51.9857 27.3923 52.1115C27.2297 52.2719 27.2297 52.2719 27.0637 52.4356C26.5157 53.0492 26.4528 53.5536 26.4717 54.356C26.5606 55.0583 26.9415 55.4233 27.4473 55.8809C28.0749 56.1831 28.6433 56.2514 29.3281 56.1094C30.2214 55.611 30.8943 54.86 31.6078 54.1384C31.7452 54.0005 31.8828 53.8625 32.0203 53.7247C32.3802 53.3637 32.7393 53.0019 33.0981 52.64C33.4653 52.2699 33.8332 51.9007 34.2011 51.5314C34.9213 50.8082 35.6406 50.0841 36.3594 49.3594C36.8539 49.5704 37.1687 49.8548 37.5448 50.2361C37.6668 50.359 37.7888 50.482 37.9146 50.6087C37.9791 50.6743 38.0436 50.74 38.1101 50.8077C38.3128 51.014 38.5164 51.2195 38.7203 51.4247C39.2263 51.9344 39.7307 52.4457 40.2352 52.9569C40.662 53.3893 41.0894 53.821 41.5175 54.252C41.7178 54.4542 41.9173 54.6571 42.1167 54.8601C42.2387 54.9831 42.3607 55.106 42.4865 55.2327C42.6469 55.3953 42.6469 55.3953 42.8106 55.5613C43.4242 56.1093 43.9286 56.1722 44.731 56.1533C45.4333 56.0644 45.7983 55.6835 46.2559 55.1777C46.5581 54.5501 46.6264 53.9817 46.4844 53.2969C45.986 52.4036 45.235 51.7307 44.5134 51.0172C44.3754 50.8798 44.2375 50.7422 44.0997 50.6047C43.7387 50.2448 43.3769 49.8858 43.015 49.5269C42.6449 49.1598 42.2757 48.7918 41.9064 48.4239C41.1832 47.7037 40.4591 46.9844 39.7344 46.2656C39.9454 45.7711 40.2298 45.4563 40.6111 45.0802C40.734 44.9582 40.857 44.8362 40.9837 44.7105C41.0493 44.6459 41.115 44.5814 41.1827 44.515C41.389 44.3122 41.5944 44.1086 41.7997 43.9047C42.3094 43.3987 42.8207 42.8943 43.3319 42.3898C43.7643 41.963 44.196 41.5356 44.627 41.1075C44.8291 40.9072 45.0321 40.7077 45.2351 40.5083C45.358 40.3863 45.481 40.2643 45.6077 40.1386C45.7703 39.9781 45.7703 39.9781 45.9363 39.8144C46.4843 39.2008 46.5472 38.6964 46.5283 37.8941C46.4394 37.1917 46.0585 36.8268 45.5527 36.3692C44.9251 36.0669 44.3567 35.9987 43.6719 36.1406C42.7786 36.6391 42.1057 37.39 41.3922 38.1116C41.2548 38.2496 41.1172 38.3875 40.9797 38.5253C40.6198 38.8863 40.2607 39.2481 39.9019 39.6101C39.5347 39.9801 39.1668 40.3493 38.7989 40.7186C38.0787 41.4419 37.3594 42.166 36.6406 42.8906C36.1461 42.6796 35.8313 42.3952 35.4552 42.0139C35.3332 41.891 35.2112 41.768 35.0854 41.6414C35.0209 41.5757 34.9564 41.51 34.8899 41.4423C34.6872 41.236 34.4836 41.0306 34.2797 40.8253C33.7737 40.3156 33.2693 39.8043 32.7648 39.2931C32.338 38.8608 31.9106 38.429 31.4825 37.998C31.2822 37.7959 31.0827 37.5929 30.8833 37.3899C30.7613 37.267 30.6393 37.144 30.5135 37.0173C30.4066 36.9089 30.2996 36.8005 30.1894 36.6887C29.5758 36.1407 29.0714 36.0778 28.269 36.0967C27.5667 36.1856 27.2017 36.5665 26.7441 37.0723Z"
                                                fill="#F65C5C"/>
                                            <path
                                                d="M41.0007 2.81252C41.0913 2.84341 41.1819 2.87429 41.2752 2.90611C44.9075 4.17335 48.0192 6.80154 49.7239 10.2678C50.6178 12.1825 51.1475 14.1237 51.1697 16.2422C51.1737 16.3722 51.1778 16.5022 51.182 16.6361C51.1896 17.4487 51.113 17.9213 50.5632 18.5625C50.0261 19.0728 49.5953 19.1686 48.8757 19.1953C48.1561 19.1686 47.7253 19.0728 47.1882 18.5625C46.6421 17.8791 46.542 17.3805 46.4939 16.541C46.2404 13.557 44.9776 10.9619 42.6882 9.00002C40.4976 7.40238 37.917 6.66023 35.2175 6.96096C32.4101 7.40189 30.0048 8.81891 28.3038 11.1237C27.167 12.8737 26.7266 14.5448 26.5392 16.5945C26.4339 17.631 26.2284 18.1681 25.4617 18.8965C24.7353 19.2337 24.0694 19.231 23.282 19.125C22.5837 18.8008 22.2392 18.3904 21.8757 17.7188C21.7942 17.2569 21.7956 16.8069 21.8054 16.3389C21.8071 16.2086 21.8088 16.0784 21.8106 15.9441C21.9228 11.9793 23.9072 8.49408 26.7207 5.79092C30.4016 2.398 36.2746 1.192 41.0007 2.81252Z"
                                                fill="#F65C5C"/>
                                        </svg>
                                        <h2>Failed!</h2>
                                        <p>{transaction.message}</p>
                                        <button style={buttonStyleFail} onClick={() => navigate('/profile')}>
                                            Try Again
                                        </button>
                                    </div>
                                    <button className="close-btn-com" onClick={() => navigate('/profile')}>X</button>
                                </div>
                            </>
                        )
                    )}
                </div>
            </div>
        );
    } else {
        return (
            <Loading/>
        );
    }
};

const Checkout = ({isTransaction}) => {
    const [paymentOption, setPaymentOption] = useState(null);

    useEffect(() => {
        getSelectedPricingPackage(!isTransaction).then((selectedPricingPackage) => {
            updatePaymentOption(selectedPricingPackage);
        }).catch(null);
    }, []);

    const updatePaymentOption = (selectedPricingPackage, customerSessionClientSecret = null) => {
        if (selectedPricingPackage) {
            let amount = selectedPricingPackage.annualSelected ? (selectedPricingPackage.annual_price * 12).toFixed(2) : selectedPricingPackage.monthly_price.toFixed(2);
            if ((amount - Math.floor(amount)) !== 0) {
                let parts = amount.toString().split('.');
                amount = parseInt(parts[0] + (parts[1] || 0));
            } else {
                amount = parseInt(amount.toString().split('.')[0] + '00');
            }
            const tempPaymentOption = {
                mode: 'subscription',
                amount: amount,
                currency: 'eur',
                appearance: {
                    variables: {
                        fontFamily: 'Arial, sans-serif',
                        borderRadius: '120px',
                    }
                },
                customerSessionClientSecret,
            };
            setPaymentOption(tempPaymentOption);
        }
    }

    if (paymentOption || isTransaction) {
        return (
            <Elements stripe={stripePromise} options={paymentOption}>
                <CheckoutForm updatePaymentOption={updatePaymentOption} isTransaction={isTransaction}/>
            </Elements>
        )
    } else {
        return (
            <Loading/>
        );
    }
}

export default Checkout;
